import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Copy,
  MagnifyingGlass,
  Info,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STEPS = ["Sheet access", "MySaaS credentials", "Column mapping"];

export default function AddConnection() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saEmail, setSaEmail] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [resolving, setResolving] = useState(false);
  const [sheet, setSheet] = useState(null); // { spreadsheet_id, spreadsheet_name, tabs, tab, rows }
  const [label, setLabel] = useState("");
  const [creds, setCreds] = useState({
    business_id: "",
    location_id: "",
    template_id: "",
    mysaas_api_key: "",
    send_reminders: false,
  });
  const [mapping, setMapping] = useState({
    name_col: 0,
    email_col: 1,
    phone_col: 2,
    header_row: true,
    trigger_col: null,
    trigger_value: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const verifyMysaas = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/mysaas/verify", {
        api_key: creds.mysaas_api_key,
        business_id: creds.business_id,
      });
      setCreds((c) => ({
        ...c,
        _verified: true,
        _businessName: data.business_name,
        _locations: data.locations,
        location_id: data.locations.length === 1 ? data.locations[0].id : "",
        template_id: "",
      }));
      setTemplates([]);
      toast.success(`Verified: ${data.business_name}`);
    } catch (e) {
      setCreds((c) => ({ ...c, _verified: false, _locations: [], location_id: "" }));
      toast.error(e.response?.data?.detail || "Could not verify MySaaS credentials");
    } finally {
      setVerifying(false);
    }
  };

  // Fetch templates whenever a location is picked
  useEffect(() => {
    if (!creds._verified || !creds.location_id) return;
    setLoadingTemplates(true);
    api.post("/mysaas/templates", {
      api_key: creds.mysaas_api_key,
      business_id: creds.business_id,
      location_id: creds.location_id,
    })
      .then((r) => setTemplates(r.data.templates || []))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load templates"))
      .finally(() => setLoadingTemplates(false));
  }, [creds._verified, creds.location_id, creds.business_id, creds.mysaas_api_key]);

  useEffect(() => {
    api.get("/sheets/service-account")
      .then((r) => setSaEmail(r.data.email))
      .catch((e) => toast.error(e.response?.data?.detail || "Google Sheets not configured"));
  }, []);

  const copyEmail = () => {
    navigator.clipboard.writeText(saEmail);
    toast.success("Service account email copied");
  };

  const resolve = async (tabOverride) => {
    if (!urlInput.trim()) return;
    setResolving(true);
    try {
      const { data } = await api.post("/sheets/resolve", {
        url_or_id: urlInput.trim(),
        tab: tabOverride || null,
      });
      setSheet(data);
      if (!label) setLabel(data.spreadsheet_name);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read this sheet");
      setSheet(null);
    } finally {
      setResolving(false);
    }
  };

  const changeTab = async (t) => resolve(t);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { mysaas_api_key, business_id, location_id, template_id, send_reminders, _template_name, _template_channel } = creds;
      await api.post("/connections", {
        label,
        spreadsheet_id: sheet.spreadsheet_id,
        spreadsheet_name: sheet.spreadsheet_name,
        sheet_tab: sheet.tab,
        mysaas_api_key,
        business_id,
        location_id,
        template_id,
        template_name: _template_name || null,
        template_channel: _template_channel || null,
        send_reminders,
        mapping: {
          ...mapping,
          trigger_col: mapping.trigger_col === null || mapping.trigger_col === "none" ? null : Number(mapping.trigger_col),
          trigger_value: mapping.trigger_value || null,
        },
      });
      toast.success("Connection created — polling every 60s");
      navigate("/connections");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create connection");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-[1100px]">
      <button
        data-testid="back-btn"
        onClick={() => navigate(-1)}
        className="text-sm text-neutral-500 hover:text-black inline-flex items-center gap-1 mb-6"
      >
        <ArrowLeft weight="bold" size={14} /> Back
      </button>

      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-2">Add connection</div>
        <h1 className="font-display text-5xl font-black tracking-tight">Wire it up</h1>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-3 mb-10">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-3">
            <div
              className={`h-8 min-w-8 px-3 flex items-center gap-2 border ${
                i === step
                  ? "border-black bg-black text-white"
                  : i < step
                  ? "border-black text-black"
                  : "border-neutral-300 text-neutral-400"
              }`}
            >
              <span className="font-mono-tech text-xs">0{i + 1}</span>
              <span className="text-xs font-semibold uppercase tracking-widest">{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className="h-px w-8 bg-neutral-300" />}
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="bg-white border border-stone-200 p-8 space-y-6">
          {/* Share instruction */}
          <div className="border border-black bg-stone-50 p-5">
            <div className="flex items-start gap-3">
              <Info weight="duotone" size={22} className="text-black shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Step 1 · Share your sheet</div>
                <div className="font-display text-lg font-bold mb-2">Add our service account as a Viewer</div>
                <div className="flex items-center gap-2 bg-white border border-stone-300 px-3 py-2 font-mono-tech text-sm">
                  <span className="truncate flex-1" data-testid="sa-email">{saEmail || "(loading…)"}</span>
                  <button
                    data-testid="copy-sa-email"
                    onClick={copyEmail}
                    disabled={!saEmail}
                    className="text-xs font-semibold text-[#F58021] hover:underline inline-flex items-center gap-1"
                  >
                    <Copy size={13} /> Copy
                  </button>
                </div>
                <ol className="text-sm text-neutral-600 mt-3 space-y-1 list-decimal list-inside">
                  <li>Open your Google Sheet.</li>
                  <li>Click <b>Share</b> and paste the email above.</li>
                  <li>Set access to <b>Viewer</b> and share.</li>
                </ol>
              </div>
            </div>
          </div>

          {/* URL input */}
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">
              Google Sheet URL or ID
            </Label>
            <div className="flex gap-3">
              <Input
                data-testid="sheet-url-input"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/1abc…"
                className="h-11 font-mono-tech text-sm"
                onKeyDown={(e) => { if (e.key === "Enter") resolve(); }}
              />
              <Button
                data-testid="verify-sheet-btn"
                onClick={() => resolve()}
                disabled={!urlInput || resolving}
                className="bg-black hover:bg-neutral-800 text-white h-11 px-5 gap-2"
              >
                <MagnifyingGlass weight="bold" size={16} />
                {resolving ? "Checking…" : "Verify access"}
              </Button>
            </div>
          </div>

          {sheet && (
            <div className="border-t border-stone-200 pt-6 space-y-4">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle weight="fill" size={18} /> <span className="text-sm font-semibold">Access verified — <span className="font-mono-tech">{sheet.spreadsheet_name}</span></span>
              </div>

              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <Label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Connection label</Label>
                  <Input data-testid="label-input" value={label} onChange={(e) => setLabel(e.target.value)} className="h-11" />
                </div>
                <div className="w-56">
                  <Label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Sheet tab</Label>
                  <Select value={sheet.tab} onValueChange={changeTab}>
                    <SelectTrigger data-testid="tab-select" className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {sheet.tabs.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <PreviewTable rows={sheet.rows} />
            </div>
          )}

          <div className="flex justify-end pt-4">
            <Button
              data-testid="step0-next"
              disabled={!sheet || !label}
              onClick={() => setStep(1)}
              className="bg-[#F58021] hover:bg-[#D6691A] text-white h-11 px-6 gap-2"
            >
              Continue <ArrowRight weight="bold" size={16} />
            </Button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="bg-white border border-stone-200 p-8 space-y-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500">Step 2</div>
            <h2 className="font-display text-2xl font-bold">MySaaS credentials</h2>
            <p className="text-neutral-500 text-sm mt-1">Paste your API key and business ID, then pick a location.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="MySaaS API key" testid="api-key" value={creds.mysaas_api_key} onChange={(v) => setCreds({ ...creds, mysaas_api_key: v, _verified: false })} placeholder="pk_live_..." type="password" />
            <Field label="Business ID" testid="business-id" value={creds.business_id} onChange={(v) => setCreds({ ...creds, business_id: v, _verified: false, location_id: "" })} placeholder="68c0..." />
          </div>

          <div>
            <Button
              data-testid="verify-mysaas-btn"
              onClick={verifyMysaas}
              disabled={!creds.mysaas_api_key || !creds.business_id || verifying}
              className="bg-black hover:bg-neutral-800 text-white h-11 px-5 gap-2"
            >
              <MagnifyingGlass weight="bold" size={16} />
              {verifying ? "Verifying…" : creds._verified ? "Re-verify" : "Verify with MySaaS"}
            </Button>
          </div>

          {creds._verified && (
            <div className="border-t border-stone-200 pt-6 space-y-4">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle weight="fill" size={18} /> <span className="text-sm font-semibold">{creds._businessName || "Business"} verified · {(creds._locations || []).length} location(s)</span>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Location</Label>
                <Select value={creds.location_id} onValueChange={(v) => setCreds({ ...creds, location_id: v })}>
                  <SelectTrigger data-testid="location-select" className="h-11">
                    <SelectValue placeholder="Choose a location…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(creds._locations || []).map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name} · <span className="font-mono-tech text-xs">{loc.id}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Template</Label>
                <Select
                  value={creds.template_id}
                  onValueChange={(v) => {
                    const t = templates.find((x) => x.id === v);
                    setCreds({
                      ...creds,
                      template_id: v,
                      _template_name: t?.name || "",
                      _template_channel: t?.channel || "",
                    });
                  }}
                  disabled={loadingTemplates || templates.length === 0}
                >
                  <SelectTrigger data-testid="template-select" className="h-11">
                    <SelectValue
                      placeholder={loadingTemplates ? "Loading templates…" : templates.length === 0 ? "No templates found" : "Choose a template…"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        <span className="inline-flex items-center gap-2">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${channelBadgeClass(t.channel)}`}>
                            {t.channel || "?"}
                          </span>
                          {t.name || t.id}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between border border-stone-200 p-4 rounded-md bg-stone-50">
                <div>
                  <div className="font-semibold text-sm">Send reminders</div>
                  <div className="text-xs text-neutral-500">Auto-schedule reminder invites via MySaaS.</div>
                </div>
                <Switch data-testid="send-reminders-switch" checked={creds.send_reminders} onCheckedChange={(v) => setCreds({ ...creds, send_reminders: v })} />
              </div>
            </div>
          )}

          <div className="flex justify-between pt-2">
            <Button variant="outline" onClick={() => setStep(0)} data-testid="step1-back" className="h-11 gap-2">
              <ArrowLeft weight="bold" size={16} /> Back
            </Button>
            <Button
              data-testid="step1-next"
              disabled={!creds._verified || !creds.location_id || !creds.template_id}
              onClick={() => setStep(2)}
              className="bg-[#F58021] hover:bg-[#D6691A] text-white h-11 px-6 gap-2"
            >
              Continue <ArrowRight weight="bold" size={16} />
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="bg-white border border-stone-200 p-8 space-y-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500">Step 3</div>
            <h2 className="font-display text-2xl font-bold">Column mapping</h2>
            <p className="text-neutral-500 text-sm mt-1">Point us at the columns for name, email and phone. Column A = 0.</p>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <ColumnField label="Name column" testid="map-name" value={mapping.name_col} onChange={(v) => setMapping({ ...mapping, name_col: v })} />
            <ColumnField label="Email column" testid="map-email" value={mapping.email_col} onChange={(v) => setMapping({ ...mapping, email_col: v })} />
            <ColumnField label="Phone column" testid="map-phone" value={mapping.phone_col} onChange={(v) => setMapping({ ...mapping, phone_col: v })} />
          </div>

          <div className="flex items-center justify-between border border-stone-200 p-4 rounded-md bg-stone-50">
            <div>
              <div className="font-semibold text-sm">First row is header</div>
              <div className="text-xs text-neutral-500">We&apos;ll skip row 1 when it&apos;s a header.</div>
            </div>
            <Switch data-testid="header-row-switch" checked={mapping.header_row} onCheckedChange={(v) => setMapping({ ...mapping, header_row: v })} />
          </div>

          <div className="border border-stone-200 p-4 rounded-md bg-stone-50 space-y-3">
            <div>
              <div className="font-semibold text-sm">Trigger column <span className="font-normal text-neutral-500">(optional — recommended for repeat customers)</span></div>
              <div className="text-xs text-neutral-500">Only fire an invite when this column is filled — e.g. a &ldquo;Service Date&rdquo; column. When set, each new trigger value re-invites the same customer (e.g. every new service date fires a fresh invite). Leave blank to invite each customer only once, ever.</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Trigger column</Label>
                <Select
                  value={mapping.trigger_col === null || mapping.trigger_col === undefined ? "none" : String(mapping.trigger_col)}
                  onValueChange={(v) => setMapping({ ...mapping, trigger_col: v === "none" ? null : parseInt(v, 10) })}
                >
                  <SelectTrigger data-testid="trigger-col-select" className="h-11 font-mono-tech">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None (fire on any new row) —</SelectItem>
                    {Array.from({ length: 12 }).map((_, i) => (
                      <SelectItem key={i} value={String(i)}>{String.fromCharCode(65 + i)} · col {i}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">Must equal <span className="lowercase text-neutral-400">(optional)</span></Label>
                <Input
                  data-testid="trigger-value-input"
                  value={mapping.trigger_value || ""}
                  onChange={(e) => setMapping({ ...mapping, trigger_value: e.target.value })}
                  placeholder="e.g. SEND · leave blank for &quot;non-empty&quot;"
                  className="h-11 font-mono-tech"
                  disabled={mapping.trigger_col === null || mapping.trigger_col === undefined}
                />
              </div>
            </div>
          </div>

          {sheet?.rows?.length > 0 && <PreviewTable rows={sheet.rows} highlight={mapping} />}

          <div className="flex justify-between pt-2">
            <Button variant="outline" onClick={() => setStep(1)} data-testid="step2-back" className="h-11 gap-2">
              <ArrowLeft weight="bold" size={16} /> Back
            </Button>
            <Button
              data-testid="submit-connection"
              disabled={submitting}
              onClick={submit}
              className="bg-[#F58021] hover:bg-[#D6691A] text-white h-11 px-6 gap-2"
            >
              {submitting ? "Creating…" : "Create connection"} <CheckCircle weight="bold" size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function channelBadgeClass(channel) {
  const c = (channel || "").toLowerCase();
  if (c === "email") return "bg-sky-100 text-sky-800 border border-sky-200";
  if (c === "sms") return "bg-emerald-100 text-emerald-800 border border-emerald-200";
  if (c === "whatsapp") return "bg-green-100 text-green-800 border border-green-200";
  return "bg-neutral-100 text-neutral-700 border border-neutral-200";
}

function Field({ label, value, onChange, placeholder, type, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">{label}</Label>
      <Input data-testid={testid} type={type || "text"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-11 font-mono-tech" />
    </div>
  );
}

function ColumnField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-neutral-500 mb-2 block">{label}</Label>
      <Select value={String(value)} onValueChange={(v) => onChange(parseInt(v, 10))}>
        <SelectTrigger data-testid={testid} className="h-11 font-mono-tech">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Array.from({ length: 12 }).map((_, i) => (
            <SelectItem key={i} value={String(i)}>{String.fromCharCode(65 + i)} · col {i}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function PreviewTable({ rows, highlight }) {
  if (!rows || rows.length === 0) return <div className="text-neutral-500 text-sm">No preview rows.</div>;
  const width = Math.max(...rows.map((r) => r.length));
  return (
    <div className="overflow-x-auto border border-stone-200">
      <table className="w-full text-xs font-mono-tech">
        <thead className="bg-stone-100">
          <tr>
            {Array.from({ length: width }).map((_, i) => {
              const isHi =
                highlight &&
                (highlight.name_col === i || highlight.email_col === i || highlight.phone_col === i);
              const tag =
                highlight?.name_col === i ? "NAME" :
                highlight?.email_col === i ? "EMAIL" :
                highlight?.phone_col === i ? "PHONE" : null;
              return (
                <th key={i} className={`text-left py-2 px-3 border-r border-stone-200 last:border-0 ${isHi ? "bg-[#F58021] text-white" : "text-neutral-500"}`}>
                  {String.fromCharCode(65 + i)} {tag && <span className="ml-1 opacity-80">· {tag}</span>}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className="border-t border-stone-200">
              {Array.from({ length: width }).map((_, ci) => (
                <td key={ci} className="py-2 px-3 border-r border-stone-100 last:border-0 truncate max-w-[180px]">
                  {row[ci] ?? ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
